import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles, Plus, AlertCircle, Check } from 'lucide-react'
import { toast } from 'sonner'
import { reconciliationApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { BankTransaction, AISuggestionType } from '@/types'
import { cn } from '@/lib/utils'

interface CategorySuggestionBadgeProps {
  transaction: BankTransaction
  onAccepted?: () => void
}

export function CategorySuggestionBadge({ transaction, onAccepted }: CategorySuggestionBadgeProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [autoApprove, setAutoApprove] = useState(true)
  const [categoryName, setCategoryName] = useState(transaction.suggested_category_name || '')
  const [categoryDescription, setCategoryDescription] = useState(transaction.suggested_category_description || '')
  const queryClient = useQueryClient()

  const suggestionType = transaction.ai_suggestion_type as AISuggestionType | undefined

  const acceptMutation = useMutation({
    mutationFn: (data: { createNew: boolean }) => 
      reconciliationApi.transactions.acceptCategorySuggestion(transaction.transaction_id, {
        category_id: data.createNew ? undefined : transaction.suggested_category_id,
        create_new_category: data.createNew,
        category_name: data.createNew ? categoryName : undefined,
        category_description: data.createNew ? categoryDescription : undefined,
        vendor_name: transaction.ai_vendor_cleaned_name,
        auto_approve: autoApprove,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      setDialogOpen(false)
      if (response.category_created) {
        toast.success(`Category "${response.category_name}" created and applied`)
      } else {
        toast.success('Category suggestion accepted')
      }
      onAccepted?.()
    },
    onError: () => {
      toast.error('Failed to accept category suggestion')
    },
  })

  // Don't show anything if no AI suggestion or already accepted
  if (!suggestionType || suggestionType === 'ACCEPTED') {
    return null
  }

  const handleAcceptExisting = () => {
    acceptMutation.mutate({ createNew: false })
  }

  const handleAcceptNew = () => {
    if (!categoryName.trim()) {
      toast.error('Category name is required')
      return
    }
    acceptMutation.mutate({ createNew: true })
  }

  // Render based on suggestion type
  if (suggestionType === 'EXISTING') {
    return (
      <Badge 
        variant="outline" 
        className="cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900 border-purple-300 text-purple-700 dark:text-purple-300"
        onClick={handleAcceptExisting}
      >
        <Sparkles className="h-3 w-3 mr-1" />
        Accept AI Category
      </Badge>
    )
  }

  if (suggestionType === 'NEW') {
    return (
      <>
        <Badge 
          variant="outline" 
          className="cursor-pointer hover:bg-green-100 dark:hover:bg-green-900 border-green-300 text-green-700 dark:text-green-300"
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Create Category: {transaction.suggested_category_name}
        </Badge>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                AI Suggests New Category
              </DialogTitle>
              <DialogDescription>
                AI couldn't find a matching category and suggests creating a new one.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="rounded-lg border p-3 bg-muted/50">
                <p className="text-sm text-muted-foreground">Transaction:</p>
                <p className="font-medium">{transaction.description}</p>
                <p className={cn(
                  'text-sm font-medium mt-1',
                  transaction.direction === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                )}>
                  {transaction.direction === 'CREDIT' ? '+' : '-'}
                  {transaction.currency} {Math.abs(transaction.amount).toLocaleString()}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category-name">Category Name</Label>
                <Input
                  id="category-name"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  placeholder="Enter category name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category-description">Description (optional)</Label>
                <Textarea
                  id="category-description"
                  value={categoryDescription}
                  onChange={(e) => setCategoryDescription(e.target.value)}
                  placeholder="Describe what expenses belong in this category"
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="auto-approve"
                  checked={autoApprove}
                  onCheckedChange={(checked) => setAutoApprove(checked as boolean)}
                />
                <Label htmlFor="auto-approve" className="text-sm">
                  Auto-approve transaction after creating category
                </Label>
              </div>

              {transaction.ai_reasoning && (
                <div className="rounded-lg border p-3 bg-purple-50 dark:bg-purple-950">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> AI Reasoning:
                  </p>
                  <p className="text-sm italic">{transaction.ai_reasoning}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAcceptNew}
                disabled={acceptMutation.isPending || !categoryName.trim()}
              >
                <Check className="h-4 w-4 mr-1" />
                Create Category
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  if (suggestionType === 'NEEDS_REVIEW') {
    return (
      <Badge
        variant="outline"
        className="border-amber-300 text-amber-700 dark:text-amber-300"
      >
        <AlertCircle className="h-3 w-3 mr-1" />
        Needs Review
      </Badge>
    )
  }

  return null
}

